import React from "react";
import _ from "lodash";
import clsx from "clsx";

import {makeStyles} from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import Badge from "@material-ui/core/Badge";

import {withEuroOrDash} from "../../../../../../utils/utils";
import {TRANSACTION_TYPE_VALUES} from "../../../../constants";
import {getErrorsWithout, getPortfolioId} from "./utils";
import {portfolioTransactionsAmount} from "../../../../utils";
import PortfolioConfig from "./PortfolioConfig";
import {createInstrumentHandlers} from "../tradingOperations";
import {getDepotStrategyName, getModelPortfolioCode} from "../../../../../../components/CustomersDataProvider/utils";
import TruncatedText from "../../../../../../components/TruncateText";
import {getPortfolioInstrumentsData} from "../../../TradeStep/CombinedTradeStep";

import styles from "../../styles";

const useStyles = makeStyles(styles);

const TRANSACTION_KEYS = [
  TRANSACTION_TYPE_VALUES.BUY,
  TRANSACTION_TYPE_VALUES.SELL,
  TRANSACTION_TYPE_VALUES.SWITCH,
  TRANSACTION_TYPE_VALUES.SAVINGS_PLAN,
  TRANSACTION_TYPE_VALUES.PAYOUT_PLAN,
  TRANSACTION_TYPE_VALUES.SWITCH_PLAN,
];


const PortfolioCard = ({portfolio, selected, onClick, isVirtual, hasError, handlePortfolioChange, dataService}) => {
  const classes = useStyles();

  const name = portfolio.data.name;

  const borderLeftColor = portfolio.data.color;
  const ordersCount = TRANSACTION_KEYS.reduce(
    (total, key) => total + _.get(portfolio, ['transactions', key], []).length,
    0
  );
  const amount = portfolioTransactionsAmount(portfolio);
  const hasOrders = ordersCount !== 0;

  const onConfigSave = React.useCallback(async (changes) => {
    const portfolioChanges = {...changes};

    if (changes.hasOwnProperty('depotType')) {
      portfolio.depotType = _.find(portfolio.availableDepotTypes, (dt) => dt.value == changes.depotType);
      portfolioChanges.depotType = portfolio.depotType;

      const handlers = createInstrumentHandlers(portfolio, dataService, isVirtual);
      if (!portfolio.isModelportfolio) {
        for (let transactionType of [TRANSACTION_TYPE_VALUES.BUY, TRANSACTION_TYPE_VALUES.SELL, TRANSACTION_TYPE_VALUES.SWITCH]) {
          try {
            const handler = handlers[transactionType];
            if (!handler || _.isEmpty(portfolio.transactions[transactionType])) continue;
            const instruments = getPortfolioInstrumentsData(portfolio.transactions[transactionType], true);
            await handler.getCustodianAndFilterInstruments(instruments, true);
          } catch (error) {
            console.log('Error during instruments validation.', error);
          }
        }
      }

      portfolioChanges.transactions = {...portfolio.transactions};
      portfolioChanges.trading_errors = getErrorsWithout(portfolio, 'trading_errors', 'portfolio');
    }

    handlePortfolioChange(portfolio, portfolioChanges);
  }, [portfolio, dataService, isVirtual, handlePortfolioChange]);

  const categoryName = React.useMemo(() => {
    let { identifiers } = portfolio.data;
    const strategyName = getDepotStrategyName(identifiers);
    const mpNumber = getModelPortfolioCode(identifiers);

    return [mpNumber, strategyName].filter(Boolean).join(' - ');
  }, [portfolio.isModelportfolio])

  return (
    <div
      className={`${classes.portfolioCard} ${selected ? classes.portfolioCardSelected : ''} ${hasError ? classes.portfolioCardError : ''}`}
      style={borderLeftColor ? { borderLeft: `5px solid ${borderLeftColor}` } : undefined}
      onClick={onClick}
      role="button"
      data-id={`portfolio-card-${getPortfolioId(portfolio, isVirtual)}`}
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); }}
      aria-selected={selected}
    >
      <Grid container spacing={1} alignItems={"center"}>
        <Grid item xs style={{flexShrink: 1, minWidth: 100}}>
          <TruncatedText
            text={name}
            deps={[hasOrders, amount]}
            className={classes.portfolioCardName}
          />
        </Grid>
        {hasOrders && (
          <Grid item xs={'auto'} style={{textAlign: 'right'}}>
            <Badge badgeContent={ordersCount} color="primary" className={clsx(classes.tabBadge, classes.tabBadgeSmall)}>
              <b>{withEuroOrDash(amount)}</b>
            </Badge>
          </Grid>
        )}
      </Grid>

      <div style={{flex: 1}}>
        {portfolio.isModelportfolio && (
          <div className={classes.tabBadgeSmall}>
            <TruncatedText text={categoryName} />
          </div>
        )}
      </div>

      {!isVirtual && (
        <Grid container spacing={1} alignItems={"center"} wrap={'nowrap'}>
          <Grid item style={{marginLeft: 'auto', overflow: 'hidden', minWidth: 0}}>
            <PortfolioConfig portfolio={portfolio} onSave={onConfigSave} hasError={hasError} />
          </Grid>
        </Grid>
      )}
    </div>
  );
};

export default PortfolioCard;
